/* global BroadcastChannel, performance */
import React, { Component } from 'react'
import PropTypes from 'prop-types'
import normaliseUser from '@fielded/fs-api/lib/utils/normalise-user'
import { getTotalPendingUploadCount, clearLocalDatabases } from '../sync'
import withConfig from '../van-shared/hoc/withConfig'
import { AuthContext, withAuth, withUser, defaultAuthState as defaultState } from './AuthContext'

const logoutChannel = (
  typeof BroadcastChannel !== 'undefined'
    ? new BroadcastChannel('logout')
    : null
)

// re-exported for the many existing `import { withAuth, withUser } from
// './AuthenticationProvider'` call sites - the actual definitions live in
// ./AuthContext.js so van-shared/hoc/withConfig can use them too without a
// circular import back through this file.
export { withAuth, withUser }

export class AuthenticationProvider extends Component {
  static propTypes = {
    loginApi: PropTypes.object.isRequired,
    loginAsAdmin: PropTypes.object
  }

  state = {
    error: null,
    user: null,
    loggedIn: false,
    loginInProgess: false,
    initialized: false
  }

  async componentDidMount () {
    const { config } = this.props
    const session = await this.props.loginApi.getCurrentUser(config.remoteDbUrl)
    if (session) {
      this.setState({
        user: normaliseUser(session),
        loggedIn: true,
        initialized: true // <- this is expected to be here to save a re-render
      })
    }
    this.setState({ initialized: true })
  }

  // this method added for dynamic update user data after some changes
  handleUpdateUser = async () => {
    const { config } = this.props
    const session = await this.props.loginApi.getCurrentUser(config.remoteDbUrl)
    if (session) {
      this.setState({ user: normaliseUser(session) })
    }
  }

  handleLogin = async (username, password, { isAdminLogin, loggedInUserName, history, from, config }) => {
    this.setState({
      user: null,
      error: null,
      loggedIn: false,
      loginInProgress: true
    })

    try {
      const request = isAdminLogin ? this.props.loginApi.loginAsAdmin : this.props.loginApi.login

      const user = await request(username, password, { config, loggedInUserName })
      if (user.error) {
        this.setState({
          user: null,
          error: user,
          loggedIn: false,
          loginInProgress: false
        })
        return
      }

      const normalisedUser = normaliseUser(user)

      this.setState({
        error: null,
        user: normalisedUser,
        loggedIn: true,
        loginInProgress: false
      })

      window.__loginRedirectTime = performance.now()
      window.__isFreshLogin = true
      history.push(from, { from: '/login' })
    } catch (error) {
      this.setState({
        user: null,
        error: { message: error.message },
        loggedIn: false,
        loginInProgress: false
      })
    }
  }

  canUserLogout = async () => {
    const pendingUploadCount = await getTotalPendingUploadCount()
    return (pendingUploadCount === 0)
  }

  // The user doesn't do this one -- it's triggered by any 401 from pouch replication
  // or the ID endpoints, probably meaning the auth cookie expired.
  handleSessionExpired = async (user, { history, config }) => {
    if (!this.state.loggedIn) {
      // A request of a session that already logged out can still answer 401.
      return
    }

    const res = await this.props.loginApi.logout(user, { config })
    if (!res.ok) {
      this.setState({ error: res })
      return
    }

    this.setState({ ...defaultState })
    history.push('/login?sessionExpired=true')
    window.location.reload()
  }

  // We delete all local data if clearLocalData feature is enabled,
  // TODO: this is on dev only until feature is done.
  handleLogout = async (user, { history, config, clearLocalData = false }) => {
    if (logoutChannel) {
      logoutChannel.postMessage('User logged out!')
    }

    if (clearLocalData) {
      try {
        await clearLocalDatabases()
      } catch (error) {
        console.error(error)
        window.alert(`Error clearing local databases. Please contact support, or clear your local cache and refresh.`)
        return
      }
    }

    const res = await this.props.loginApi.logout(user, { config })
    if (!res.ok) {
      this.setState({ error: res })
      return
    }

    this.setState({ ...defaultState })
    history.push('/login')
    // Sync pollers and replications of the old session run until the page reloads.
    window.location.reload()
  }

  handleLogoutAcrossTabs = (user, { history, config }) => {
    if (logoutChannel) {
      logoutChannel.onmessage = (event) => {
        this.handleLogout(user, { history, config })
        logoutChannel.close()
      }
    }
  }

  handleResetLoginStatus = () => {
    this.setState({ loginInProgess: false })
  }

  refetchUser = async (history) => {
    const { config } = this.props
    const { user } = this.state
    // If one of this things is not present, we most likely are just logging in. No need to refetch
    if (!user || !config) return

    // We are fetching user object from remote DB to compare with what we are currently using during this session
    const session = await this.props.loginApi.getCurrentUser(config.remoteDbUrl)

    if (!session) {
      // If there is user but no session, it means that user was trying to access remote DB when they should not be able to. We log them out, as usually this will happen if their password was changed.
      return this.handleLogout(user, { config, history })
    }

    // If current user revision and fetched user revision don't match, we set new state that is immediately populated to the user.
    const isSameRevision = user._rev === session._rev
    if (!isSameRevision) {
      this.setState({ user: normaliseUser(session) })
    }
  }

  render () {
    const { initialized, ...stateToPass } = this.state
    if (!initialized) {
      return null
    }

    const value = {
      ...stateToPass,
      login: (...args) => this.handleLogin(...args),
      handleSessionExpired: (...args) => this.handleSessionExpired(...args),
      canUserLogout: (...args) => this.canUserLogout(...args),
      logout: (...args) => this.handleLogout(...args),
      resetLoginStatus: (...args) => this.handleResetLoginStatus(...args),
      updateUser: (...args) => this.handleUpdateUser(...args),
      refetchUser: (...args) => this.refetchUser(...args),
      handleLogoutAcrossTabs: (...args) => this.handleLogoutAcrossTabs(...args)
    }

    return (
      <AuthContext.Provider
        value={value}
      >
        {React.Children.map(this.props.children, c => c)}
      </AuthContext.Provider>
    )
  }
}

export default withConfig(AuthenticationProvider)
